import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useSendInvoiceApiV1PaymentsPaymentIdSendInvoicePost } from '#/shared/api/generated/billing/theCargoBilling'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { useCustomerEmail } from './use-customer-email'

const PAYMENT_LIST_QUERY_PREFIX = '/api/v1/payments'
const TOOLBAR_PAYMENT_QUERY_PREFIX = '/api/v1/toolbar/payment'

const TOAST_KEYS = {
  success: 'orderDetails.comment.payment.toast.sendInvoiceSuccess',
  error: 'orderDetails.comment.payment.toast.sendInvoiceError',
} as const

export interface UseSendInvoiceResult {
  customerEmail: string | undefined
  canSend: boolean
  isSending: boolean
  sendInvoice: () => Promise<void>
}

export function useSendInvoice(
  paymentId: string,
  shipmentId: string | undefined,
): UseSendInvoiceResult {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useSendInvoiceApiV1PaymentsPaymentIdSendInvoicePost()

  const customerEmail = useCustomerEmail(shipmentId)

  const sendInvoice = async () => {
    if (!customerEmail) return
    try {
      await mutation.mutateAsync({
        paymentId,
        data: { to_email: customerEmail },
      })
      queryClient.invalidateQueries({ queryKey: [PAYMENT_LIST_QUERY_PREFIX] })
      queryClient.invalidateQueries({
        queryKey: [TOOLBAR_PAYMENT_QUERY_PREFIX],
      })
      toast.success(t(TOAST_KEYS.success))
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? t(TOAST_KEYS.error))
    }
  }

  return {
    customerEmail,
    canSend: Boolean(customerEmail),
    isSending: mutation.isPending,
    sendInvoice,
  }
}
